import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import CheckboxInput from "components/common/CheckboxInput";
import TextBox from "components/common/TextBox";
import classnames from "classnames";
import styles from "./CheckboxGroup.scss";

/**
 * CheckboxGroup component
 * @returns {React.Component}
 */
export default function CheckboxGroup({
    answerChoices = [],
    value,
    sectionLabel = "",
    onChange,
    multi = false,
    disabled = false,
    className = "",
    style = {},
    name = "",
    ...rest
}) {
    const [otherText, setOtherText] = useState("");
    const [debounceTimeout, setDebounceTimeout] = useState(null);

    // Determine if "Other" is currently selected
    const isOtherSelected = multi 
        ? Array.isArray(value) && value.some(val => val && val.toLowerCase().includes("other"))
        : value && value.toLowerCase().includes("other");

    // Extract other text from value if it exists
    useEffect(() => {
        if (multi && Array.isArray(value)) {
            const hasOther = value.some(val => val && val.toLowerCase().includes("other"));
            if (!hasOther && value.length > 0) {
                // Check if any value is not in the original answerChoices
                const customValues = value.filter(val => !answerChoices.includes(val));
                if (customValues.length > 0) {
                    setOtherText(customValues[0]);
                }
            } else if (!hasOther) {
                // Clear otherText if no "Other" is selected
                setOtherText("");
            }
        } else if (!multi && value && !value.toLowerCase().includes("other") && !answerChoices.includes(value)) {
            setOtherText(value);
        } else if (!multi && (!value || value.toLowerCase().includes("other"))) {
            // Clear otherText if no custom value or "Other" is selected
            setOtherText("");
        }
    }, [value, multi, answerChoices]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
        };
    }, [debounceTimeout]);

    const handleCheckboxChange = (optionValue) => {
        if (disabled) return;

        if (multi) {
            const currentValues = Array.isArray(value) ? value : [];
            let updated;
            
            if (currentValues.includes(optionValue)) {
                updated = currentValues.filter((val) => val !== optionValue);
                // If deselecting "Other", remove any custom text that was added with it
                if (optionValue && optionValue.toLowerCase().includes("other")) {
                    // Remove any custom text that's not in the original answerChoices
                    updated = updated.filter(val => answerChoices.includes(val));
                    setOtherText("");
                }
            } else {
                updated = [...currentValues, optionValue];
            }
            
            if (onChange) {
                onChange({ name, value: updated });
            }
        } else {
            if (onChange) {
                // For single select, if deselecting "Other", clear any custom text
                if (!optionValue || !optionValue.toLowerCase().includes("other")) {
                    // If current value is custom text (not in answerChoices), clear it
                    if (value && !answerChoices.includes(value)) {
                        onChange({ name, value: "" });
                    } else {
                        onChange({ name, value: optionValue });
                    }
                    setOtherText("");
                } else {
                    onChange({ name, value: optionValue });
                }
            }
        }
    };

    const handleOtherTextChange = ({ value: textValue }) => {
        setOtherText(textValue);
        
        // Clear existing timeout
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
        }
        
        // Set new timeout for debounced update
        const timeoutId = setTimeout(() => {
            if (onChange) {
                if (multi) {
                    const currentValues = Array.isArray(value) ? value : [];
                    const hasOther = currentValues.some(val => val && val.toLowerCase().includes("other"));

                    if (hasOther) {
                        // Find the "Other" option from answerChoices
                        const otherOption = answerChoices.find(choice => {
                            const choiceValue = typeof choice === 'string' ? choice : choice.value;

                            return choiceValue && choiceValue.toLowerCase().includes("other");
                        });

                        const otherValue = typeof otherOption === 'string' ? otherOption : otherOption?.value;
                        
                        // Remove any existing "Other" options and custom text, keep only regular choices
                        const updated = currentValues.filter(val => {
                            if (!val) return false;
                            // Keep values that are in the original answerChoices and don't contain "other"
                            const isInAnswerChoices = answerChoices.some(choice => {
                                const choiceValue = typeof choice === 'string' ? choice : choice.value;
                                return choiceValue === val;
                            });
                            return isInAnswerChoices && !val.toLowerCase().includes("other");
                        });
                        
                        if (textValue.trim()) {
                            // Add both "Other" option and custom text
                            updated.push(otherValue, textValue.trim());
                        } else {
                            // Only add "Other" option if no custom text
                            updated.push(otherValue);
                        }
                        
                        onChange({ name, value: updated });
                    }
                } else {
                    // For single select, keep "Other" selected while typing
                    const otherOption = value && value.toLowerCase().includes("other") ? value : "Other";
                    
                    if (textValue.trim()) {
                        // Use custom text as the value
                        onChange({ name, value: textValue.trim() });
                    } else {
                        // Keep "Other" selected
                        onChange({ name, value: otherOption });
                    }
                }
            }
        }, 300); // 300ms debounce
        
        setDebounceTimeout(timeoutId);
    };

    return (
        <div
            className={classnames(styles.root, "flex-col", className)}
            style={{ display: "flex", flexDirection: "column", ...style }}
            {...rest}
        >
            {sectionLabel && (
                <p className={styles.label}>{sectionLabel} {multi ? "(Multi-select)" : ""}</p>
            )}
            {answerChoices.map((option) => {
                const optionValue = typeof option === 'string' ? option : option.value;
                const optionLabel = typeof option === 'string' ? option : option.label;
                const checked = multi
                    ? Array.isArray(value) && value.includes(optionValue)
                    : value === optionValue;
                return (
                    <CheckboxInput
                        key={optionValue}
                        name={name}
                        label={optionLabel}
                        checked={checked}
                        disabled={disabled}
                        onChange={() => handleCheckboxChange(optionValue)}
                    />
                );
            })}
            {/* Render textbox if "Other" is selected */}
            {isOtherSelected && (
                <div className="">
                    <TextBox
                        name={`${name}_other`}
                        label="Please specify:"
                        value={otherText}
                        onChange={handleOtherTextChange}
                        disabled={disabled}
                        placeholder="Enter your answer..."
                    />
                </div>
            )}
        </div>
    );
}

CheckboxGroup.propTypes = {
    answerChoices: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                label: PropTypes.node.isRequired,
                value: PropTypes.any.isRequired,
                disabled: PropTypes.bool,
                small: PropTypes.bool,
                large: PropTypes.bool,
                solidIcons: PropTypes.bool,
                circle: PropTypes.bool,
                neverEmpty: PropTypes.bool,
                information: PropTypes.string,
                informationPosition: PropTypes.string,
                leftLabel: PropTypes.bool,
                "data-testid": PropTypes.string,
                extraStyles: PropTypes.object,
            })
        ])
    ).isRequired,
    sectionLabel: PropTypes.node,
    value: PropTypes.any,
    onChange: PropTypes.func,
    multi: PropTypes.bool,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    style: PropTypes.object,
    name: PropTypes.string,
};
